"use client";

const STORAGE_KEY = "venturu_pending_inquiry";
const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

export type PendingListingInquiry = {
  type: "listing";
  listingId: number;
  message: string;
  savedAt: number;
};

export type PendingAgentInquiry = {
  type: "agent";
  agentId: string;
  agentType: "SELLER_AGENT" | "BUYER_AGENT";
  message: string;
  savedAt: number;
};

export type PendingInquiry = PendingListingInquiry | PendingAgentInquiry;

export function usePendingInquiry() {
  function save(
    data: Omit<PendingListingInquiry, "savedAt"> | Omit<PendingAgentInquiry, "savedAt">,
  ) {
    try {
      const entry = { ...data, savedAt: Date.now() };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch {
      // sessionStorage unavailable (SSR, private browsing quota, etc.)
    }
  }

  function get(): PendingInquiry | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const data = JSON.parse(raw) as PendingInquiry;

      if (Date.now() - data.savedAt > MAX_AGE_MS) {
        clear();
        return null;
      }

      return data;
    } catch {
      return null;
    }
  }

  function consume(): PendingInquiry | null {
    const data = get();
    if (data) clear();
    return data;
  }

  function clear() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  return { save, get, consume, clear };
}
